/**
 * Utility functions for date and time formatting
 */

/**
 * Converts a string to sentence case (first letter capitalized, rest lowercase)
 * @param text - The text to convert
 * @returns Text in sentence case
 */
export const toSentenceCase = (text: string | null | undefined): string => {
  if (!text) return '';
  
  const trimmed = text.trim();
  if (trimmed.length === 0) return '';
  
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

/**
 * Formats a date string to a readable date and time format
 * @param dateString - ISO date string or date string
 * @returns Formatted string like "Dec 1, 2025 12:51 PM"
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Not available';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Formats a date string to date only (no time)
 * @param dateString - ISO date string or date string
 * @returns Formatted string like "Dec 1, 2025"
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Not available';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Formats a date string to time only (no date)
 * @param dateString - ISO date string or date string
 * @returns Formatted string like "12:51 PM"
 */
export const formatTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Not available';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid date';
  }
};

